// Who the signed-in user is to the gate app: the gates they may operate and whether they are a
// gate admin. Loaded once per login (token) and reloaded after admin changes.

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { gateApi, type Me } from "@/lib/gate";

interface MeState {
  me: Me | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

const MeContext = createContext<MeState | null>(null);

export function MeProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!token) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- signed out: forget the profile
      setMe(null);
      return;
    }
    let cancel = false;
    setLoading(true);
    setError(null);
    gateApi
      .me(token)
      .then((d) => {
        if (!cancel) setMe({ gates: d?.gates ?? [], isAdmin: !!d?.isAdmin });
      })
      .catch((e) => {
        // (a stale token is signed out by AuthProvider's own token check)
        if (!cancel) setError(e instanceof ApiError ? e.message : "Could not load your gates.");
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [token, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return <MeContext.Provider value={{ me, loading, error, reload }}>{children}</MeContext.Provider>;
}

export function useMe() {
  const ctx = useContext(MeContext);
  if (!ctx) throw new Error("useMe must be used within MeProvider");
  return ctx;
}
